import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';

/** Pointer travel (px) after which a press stops being a tap and commits to an axis. */
const DRAG_THRESHOLD_PX = 5;

@Component({
  standalone: true,
  selector: 'app-ev-switch',
  templateUrl: './ev-switch.component.html',
  styleUrls: ['./ev-switch.component.scss'],
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvSwitchComponent {

  /**
   * Two-way bound value (0 = off, 1 = on). A model signal rather than a plain @Input:
   * toggling writes the signal, which marks this OnPush view dirty and pushes the new
   * value to the parent's signal in one step. Assigning to a plain @Input notified
   * neither, so the thumb icon only repainted when something else happened to
   * re-render the switch.
   */
  readonly value = model<number>(0);

  readonly disabled = input<boolean>(false);
  readonly ariaLabel = input<string>('');

  /** Optional SVG asset path shown inside the thumb when the switch is ON. */
  readonly onSvg = input<string | undefined>(undefined);
  /** Optional SVG asset path shown inside the thumb when the switch is OFF. */
  readonly offSvg = input<string | undefined>(undefined);

  /** Fires after a user-driven toggle, once `value` has been updated. */
  readonly valueChangeEvent = output<void>();

  readonly isOn = computed(() => this.value() === 1);
  readonly activeSvg = computed(() => this.isOn() ? this.onSvg() : this.offSvg());

  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly thumbRef = viewChild.required<ElementRef<HTMLElement>>('thumb');

  // Gestures come in over two streams. Touches are followed through the touch events
  // rather than their pointer equivalents: a quick flick makes iOS Safari decide the
  // touch is a page pan and fire pointercancel, sometimes before the first pointermove,
  // which left a drag with nothing to act on and snapped the thumb back. touchmove is
  // cancellable, so blocking its default keeps the gesture ours for as long as it
  // stays horizontal. Mouse and pen keep to pointer events, which they never cancel.

  /** Id of the mouse/pen press in progress, or null when none is down on the switch. */
  private activePointerId: number | null = null;
  /** Identifier of the touch being followed, or null when no touch is down. */
  private activeTouchId: number | null = null;

  private startX = 0;
  private startY = 0;
  private startOn = false;
  /**
   * Which way the gesture went once it passed the threshold. Vertical ones are handed
   * straight back to the page, so the panel still scrolls when a finger happens to
   * come down on the switch.
   */
  private axisLock: 'horizontal' | 'vertical' | null = null;
  /** Thumb travel distance in px, measured on press so it follows the responsive tokens. */
  private travel = 0;
  /** Latest thumb offset, used when a gesture ends without usable coordinates. */
  private lastOffset = 0;
  /** Set when a drag settled the value, so the click browsers fire afterwards cannot toggle it back. */
  private swallowNextClick = false;

  /** True once the press has moved far enough to count as a drag; the thumb then follows the finger. */
  private readonly dragging = signal(false);

  @HostBinding('attr.role') readonly role = 'switch';
  @HostBinding('attr.tabindex') get tabindex(): string { return this.disabled() ? '-1' : '0'; }
  @HostBinding('attr.aria-checked') get ariaChecked(): string { return this.isOn() ? 'true' : 'false'; }
  @HostBinding('attr.aria-disabled') get ariaDisabledAttr(): string | null { return this.disabled() ? 'true' : null; }
  @HostBinding('attr.aria-label') get ariaLabelAttr(): string | null { return this.ariaLabel() || null; }
  @HostBinding('class.is-on') get isOnClass(): boolean { return this.isOn(); }
  @HostBinding('class.is-disabled') get isDisabledClass(): boolean { return this.disabled(); }
  @HostBinding('class.is-dragging') get isDraggingClass(): boolean { return this.dragging(); }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.swallowNextClick) {
      this.swallowNextClick = false;
      return;
    }
    this.toggle();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) {
      return;
    }
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.toggle();
    }
  }

  // --- touch ---------------------------------------------------------------

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    // Only the first finger drives the switch; later ones are ignored until it lifts.
    if (this.disabled() || this.activeTouchId !== null) {
      return;
    }
    const touch = event.changedTouches[0];
    if (!touch) {
      return;
    }
    this.activeTouchId = touch.identifier;
    this.beginGesture(touch.clientX, touch.clientY);
  }

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    const touch = this.trackedTouch(event);
    if (!touch) {
      return;
    }
    this.trackGesture(touch.clientX, touch.clientY);
    if (this.axisLock === 'horizontal' && event.cancelable) {
      // Claim the gesture: without this the browser can still adopt a fast flick as a
      // pan and cancel everything underneath the drag.
      event.preventDefault();
    }
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    const touch = this.trackedTouch(event);
    if (!touch) {
      return;
    }
    this.activeTouchId = null;
    this.finishGesture(this.offsetFor(touch.clientX - this.startX));
  }

  @HostListener('touchcancel', ['$event'])
  onTouchCancel(event: TouchEvent): void {
    const touch = this.trackedTouch(event);
    if (!touch) {
      return;
    }
    this.activeTouchId = null;
    // Coordinates on a cancel are not worth trusting, so settle on the last position
    // the drag actually reached.
    this.finishGesture(this.lastOffset);
  }

  // --- mouse / pen ---------------------------------------------------------

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (this.disabled() || !event.isPrimary || this.isTouch(event)) {
      return;
    }
    this.activePointerId = event.pointerId;
    this.beginGesture(event.clientX, event.clientY);
    // Capture so the gesture keeps reporting once the cursor leaves the (small) switch.
    // Synthetic pointer events carry ids the browser does not know; the drag still
    // works from the plain listeners, so a rejected capture must not abort it.
    try {
      this.hostRef.nativeElement.setPointerCapture(event.pointerId);
    } catch {
      // no capture available for this pointer
    }
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId || this.isTouch(event)) {
      return;
    }
    this.trackGesture(event.clientX, event.clientY);
    if (this.axisLock === 'vertical') {
      // Not ours after all — drop it so neither the release nor a cancel can change
      // the value, and so the released capture leaves the page free to scroll.
      this.releasePointer();
    }
  }

  @HostListener('pointerup', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId || this.isTouch(event)) {
      return;
    }
    this.releasePointer();
    this.finishGesture(this.offsetFor(event.clientX - this.startX));
  }

  @HostListener('pointercancel', ['$event'])
  onPointerCancel(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId || this.isTouch(event)) {
      return;
    }
    this.releasePointer();
    this.finishGesture(this.lastOffset);
  }

  // --- shared gesture handling ---------------------------------------------

  public toggle(): void {
    this.setValue(!this.isOn());
  }

  private beginGesture(x: number, y: number): void {
    // A press that never turns into a drag is handled by the click listener, so start clean.
    this.swallowNextClick = false;
    this.startX = x;
    this.startY = y;
    this.startOn = this.isOn();
    this.axisLock = null;
    this.hostRef.nativeElement.style.removeProperty('--_drag-x');
    // Host width is the track plus the thumb overflow on both sides, so the thumb's
    // full travel is exactly what is left once the thumb itself is subtracted.
    this.travel = Math.max(0, this.hostRef.nativeElement.offsetWidth - this.thumbRef().nativeElement.offsetWidth);
    this.lastOffset = this.startOn ? this.travel : 0;
  }

  private trackGesture(x: number, y: number): void {
    const dx = x - this.startX;
    if (this.axisLock === null) {
      const dy = y - this.startY;
      if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) {
        return;
      }
      this.axisLock = Math.abs(dx) >= Math.abs(dy) ? 'horizontal' : 'vertical';
      if (this.axisLock === 'vertical') {
        return;
      }
      this.dragging.set(true);
    }
    if (this.axisLock !== 'horizontal') {
      return;
    }
    this.lastOffset = this.offsetFor(dx);
    this.setThumbOffset(this.lastOffset);
  }

  /**
   * Ends a gesture on the state the thumb was left closest to. A press that never
   * became a drag falls through to the click listener, and a drag released back on
   * the side it started from leaves the value — and `valueChangeEvent` — alone.
   */
  private finishGesture(offset: number): void {
    const wasDragging = this.dragging();
    // The offset stays on the element: dropping `is-dragging` hands the thumb back to
    // the transition, which then animates from where the finger left it to the state
    // it settled on. Clearing it here instead would snap the thumb to the off position
    // for the frame before the class change renders. The next press clears it.
    this.dragging.set(false);
    if (!wasDragging) {
      return;
    }
    // Any click the browser still synthesises for this gesture must not toggle again.
    this.swallowNextClick = true;
    // If the track could not be measured there is no midpoint to compare against, so
    // treat the drag as a plain toggle.
    const shouldBeOn = this.travel > 0 ? offset > this.travel / 2 : !this.startOn;
    if (shouldBeOn !== this.startOn) {
      this.setValue(shouldBeOn);
    }
  }

  private setValue(on: boolean): void {
    if (this.disabled()) {
      return;
    }
    // Writing the model signal emits `valueChange` for the [(value)] binding as well.
    this.value.set(on ? 1 : 0);
    this.valueChangeEvent.emit();
  }

  /** Thumb position for a pointer delta, clamped to the track. */
  private offsetFor(dx: number): number {
    const offset = (this.startOn ? this.travel : 0) + dx;
    return Math.max(0, Math.min(this.travel, offset));
  }

  /**
   * Driven straight through the DOM rather than a signal binding: this runs on every
   * move frame and only moves the thumb, so it should not cost a change detection pass.
   */
  private setThumbOffset(offset: number): void {
    this.hostRef.nativeElement.style.setProperty('--_drag-x', `${offset}px`);
  }

  private releasePointer(): void {
    const pointerId = this.activePointerId;
    this.activePointerId = null;
    if (pointerId !== null && this.hostRef.nativeElement.hasPointerCapture(pointerId)) {
      this.hostRef.nativeElement.releasePointerCapture(pointerId);
    }
  }

  /** Touches from a pointer stream we deliberately ignore — the touch listeners own those. */
  private isTouch(event: PointerEvent): boolean {
    return event.pointerType === 'touch';
  }

  /** The touch this gesture is following, if it is among the ones the event changed. */
  private trackedTouch(event: TouchEvent): Touch | null {
    if (this.activeTouchId === null) {
      return null;
    }
    for (const touch of Array.from(event.changedTouches)) {
      if (touch.identifier === this.activeTouchId) {
        return touch;
      }
    }
    return null;
  }
}
